import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Select } from 'lego-on-react';
import { difference } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { confirm } from '../../service/Confirm/Confirm';
import { pluralizeWord } from '../../service/Pluralize/Pluralize';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';

import './RegionSelect.css';
import * as keyset from './RegionSelect.i18n';

const i18n = i18nFactory(keyset);

export interface RegionSelectProps {
    regions: Region[];
    selected: string[];
    onChange: (selected: OnChangeState) => void;
    cls?: string;
    noOneRegionSelected: boolean;
    isNewInterface?: boolean;
}

export interface RegionSelectState {
    refInited: boolean;
}

export interface Region {
    id: string;
    text: string;
    screensCount: number;
    goToRegionLocation: (regionId: string) => void;
    uncheckAllPlatforms: (regionId: string) => void;
}

export interface OnChangeState {
    add?: string[];
    remove?: string[];
}

export class RegionSelect extends React.Component<
    RegionSelectProps,
    RegionSelectState
> {
    state = {
        refInited: false
    };

    private block = 'region-select';
    private ref = React.createRef<HTMLDivElement>();

    componentDidMount() {
        this.setState({ refInited: true });
    }

    render() {
        const { noOneRegionSelected, isNewInterface } = this.props;

        const placeHolder = this.getPlaceHolder();

        return (
            <div ref={this.ref}>
                <Select
                    cls={classNames(this.props.cls, Bem.cls(this.block))}
                    menu={{
                        cls: this.block
                    }}
                    val={noOneRegionSelected ? [] : this.props.selected}
                    placeholder={placeHolder}
                    popup={
                        isNewInterface
                            ? undefined
                            : { scope: this.ref.current || undefined }
                    }
                    text={placeHolder}
                    type="check"
                    theme={'normal'}
                    size="n"
                    view={'classic'}
                    items={this.getSelectItems()}
                    maxHeight={400}
                    onChange={this.onSelectChange}
                />
            </div>
        );
    }

    private getSelectItems = () => {
        const { regions } = this.props;

        return regions.map(region => {
            return {
                text: this.getOptionText(region),
                val: region.id
            };
        });
    };

    private getOptionText = (region: Region) => {
        const {
            goToRegionLocation,
            uncheckAllPlatforms,
            text,
            screensCount,
            id
        } = region;

        return (
            <>
                <span className={Bem.cls(this.block, 'region-name')}>
                    {text}
                </span>
                <span className={Bem.cls(this.block, 'buttons-container')}>
                    <span className={Bem.cls(this.block, 'platforms-count')}>
                        {screensCount > 0
                            ? pluralizeWord(
                                  [
                                      i18n('one-screens', {
                                          count: screensCount
                                      }),
                                      i18n('some-screens', {
                                          count: screensCount
                                      }),
                                      i18n('many-screens', {
                                          count: screensCount
                                      })
                                  ],
                                  screensCount
                              )
                            : null}
                    </span>
                    {this.getGoToRegionButton(goToRegionLocation, id)}
                    {this.getUncheckButton(
                        uncheckAllPlatforms,
                        id,
                        screensCount
                    )}
                </span>
            </>
        );
    };

    private getUncheckButton = (
        callback: (regionId: string) => void,
        regionId: string,
        screensCount: number
    ) => {
        return (
            <Shape2
                cls={Bem.cls({
                    block: this.block,
                    elem: 'uncheck-button',
                    mods: { disabled: !screensCount }
                })}
                title={i18n('delete-screens')}
                width={16}
                height={16}
                icon={Shape2Icon.trashbin}
                onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();

                    if (screensCount) {
                        confirm({
                            message: i18n('delete-screens'),
                            children: (
                                <p>
                                    {pluralizeWord(
                                        [
                                            i18n(
                                                'one-uncheck-confirm-message',
                                                {
                                                    count: screensCount,
                                                    city: this.getRegionName([
                                                        regionId
                                                    ])
                                                }
                                            ),
                                            i18n(
                                                'some-uncheck-confirm-message',
                                                {
                                                    count: screensCount,
                                                    city: this.getRegionName([
                                                        regionId
                                                    ])
                                                }
                                            ),
                                            i18n(
                                                'many-uncheck-confirm-message',
                                                {
                                                    count: screensCount,
                                                    city: this.getRegionName([
                                                        regionId
                                                    ])
                                                }
                                            )
                                        ],
                                        screensCount
                                    )}
                                </p>
                            )
                        })
                            .then(() => callback(regionId))
                            .catch(() => {
                                return;
                            });
                    }
                }}
            />
        );
    };

    private getGoToRegionButton = (
        callback: (regionId: string) => void,
        regionId: string
    ) => {
        return (
            <Shape2
                cls={Bem.cls(this.block, 'go-to-button')}
                title={i18n('show-on-map')}
                width={16}
                height={16}
                icon={Shape2Icon.mapPlaceMark}
                onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();

                    callback(regionId);
                }}
            />
        );
    };

    private getPlaceHolder = () => {
        const { selected, regions } = this.props;
        const selectedLength = selected.length;

        if (selectedLength === regions.length) {
            return i18n('all-city');
        } else if (selectedLength > 3) {
            return i18n('Selected-of-regions', {
                currentRegionCount: selectedLength,
                totalRegionCount: regions.length
            });
        } else {
            return this.getRegionName(selected);
        }
    };

    private getRegionName = (regionsId: string[]) => {
        const { regions } = this.props;
        const regionsName: string[] = [];

        regions.map((region: Region) => {
            if (regionsId.includes(region.id)) {
                regionsName.push(region.text);
            }
        });

        return regionsName.length ? regionsName.join(', ') : i18n('all-city');
    };

    private getSelectTypeChange = (newSelected: string[]) => {
        const { selected } = this.props;
        const removed = difference(selected, newSelected);
        const added = difference(newSelected, selected);

        return {
            add: added,
            remove: removed
        };
    };

    private onSelectChange = (value: string[]) => {
        const { onChange } = this.props;
        const val = this.getSelectTypeChange(value);

        onChange(val);
    };
}
